import { useEffect, useCallback } from 'react';
import { isCursorAtStart, isCursorAtEnd } from '../utils/validations';

/**
 * useKeyboardShortcuts Hook
 * 
 * Manages keyboard shortcuts for transcript editor
 * 
 * @param {Object} handlers - Event handlers for different shortcuts
 * @param {Object} options - Configuration options
 */
const useKeyboardShortcuts = (handlers, options = {}) => {
  const {
    onSplit,
    onMergeUp,
    onMergeDown,
    onDelete,
    onDuplicate,
    onUndo,
    onRedo,
    onSave,
    onSearch,
    onPlayPause,
    onSkipForward,
    onSkipBackward,
    onNextBlock,
    onPreviousBlock,
    onMoveUp,
    onMoveDown,
    onSelectAll,
    onCopy,
    onPaste,
    onFullscreen,
  } = handlers;

  const {
    enabled = true,
    skipForwardTime = 5,
    skipBackwardTime = -5,
    fineTuneTime = 1,
  } = options;

  /**
   * Check if element is contentEditable
   */
  const isContentEditable = useCallback((element) => {
    return (
      element?.isContentEditable ||
      element?.getAttribute('contenteditable') === 'true'
    );
  }, []);

  /**
   * Handle keyboard events
   */
  const handleKeyDown = useCallback(
    (e) => {
      if (!enabled) return;

      const target = e.target;
      const isEditable = isContentEditable(target);
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modKey = isMac ? e.metaKey : e.ctrlKey;

      // Cmd/Ctrl + Shift + Space - Pause video (works everywhere, including when editing)
      if (modKey && e.shiftKey && e.code === 'Space') {
        e.preventDefault();
        onPlayPause?.();
        return;
      }

      // Shift + Enter - Split block
      if (e.shiftKey && e.key === 'Enter' && isEditable) {
        e.preventDefault();
        onSplit?.();
        return;
      }

      // Shift + Backspace at start - Merge up
      if (e.shiftKey && e.key === 'Backspace' && isEditable && isCursorAtStart(target)) {
        e.preventDefault();
        onMergeUp?.();
        return;
      }

      // Shift + Delete at end - Merge down
      if (e.shiftKey && e.key === 'Delete' && isEditable && isCursorAtEnd(target)) {
        e.preventDefault();
        onMergeDown?.();
        return;
      }

      // Cmd/Ctrl + Shift + Z - Undo
      if (modKey && e.shiftKey && e.key === 'z') {
        e.preventDefault();
        onUndo?.();
        return;
      }

      // Cmd/Ctrl + Shift + Y - Redo (alternative)
      if (modKey && e.shiftKey && e.key === 'y') {
        e.preventDefault();
        onRedo?.();
        return;
      }

      // Cmd/Ctrl + Shift + S - Save
      if (modKey && e.shiftKey && e.key === 's') {
        e.preventDefault();
        onSave?.();
        return;
      }

      // Cmd/Ctrl + Shift + F - Search
      if (modKey && e.shiftKey && e.key === 'f') {
        e.preventDefault();
        onSearch?.();
        return;
      }

      // Cmd/Ctrl + Shift + D - Duplicate block
      if (modKey && e.shiftKey && e.key === 'd' && !isEditable) {
        e.preventDefault();
        onDuplicate?.();
        return;
      }

      // Cmd/Ctrl + Shift + Backspace - Delete block
      if (modKey && e.shiftKey && e.key === 'Backspace' && !isEditable) {
        e.preventDefault();
        onDelete?.();
        return;
      }

      // Shift + Up/Down Arrow - Move block (when not in editable field)
      if (e.shiftKey && e.key === 'ArrowUp' && !isEditable && !modKey) {
        e.preventDefault();
        onMoveUp?.();
        return;
      }

      if (e.shiftKey && e.key === 'ArrowDown' && !isEditable && !modKey) {
        e.preventDefault();
        onMoveDown?.();
        return;
      }

      // Shift + Tab - Next block
      if (e.key === 'Tab' && e.shiftKey && !e.altKey && isEditable) {
        e.preventDefault();
        onNextBlock?.();
        return;
      }

      // Alt + Tab - Previous block
      if (e.key === 'Tab' && e.altKey && isEditable) {
        e.preventDefault();
        onPreviousBlock?.();
        return;
      }

      // Shift + Space - Play/Pause (only when not in editable field)
      if (e.shiftKey && e.code === 'Space' && !isEditable && !modKey) {
        e.preventDefault();
        onPlayPause?.();
        return;
      }

      // Shift + Arrow Left/Right - Seek video (only when not in editable field)
      if (e.shiftKey && e.key === 'ArrowLeft' && !isEditable && !modKey) {
        e.preventDefault();
        onSkipBackward?.(skipBackwardTime);
        return;
      }

      if (e.shiftKey && e.key === 'ArrowRight' && !isEditable && !modKey) {
        e.preventDefault();
        onSkipForward?.(skipForwardTime);
        return;
      }

      // Shift + J/K/L - Video controls (editor style)
      if (e.shiftKey && !isEditable) {
        if (e.key === 'j' || e.key === 'J') {
          e.preventDefault();
          onSkipBackward?.(skipBackwardTime);
          return;
        }

        if (e.key === 'k' || e.key === 'K') {
          e.preventDefault();
          onPlayPause?.();
          return;
        }

        if (e.key === 'l' || e.key === 'L') {
          e.preventDefault();
          onSkipForward?.(skipForwardTime);
          return;
        }
      }

      // Shift + F - Toggle fullscreen (works everywhere except when Cmd/Ctrl is pressed)
      if (e.shiftKey && e.code === 'KeyF' && !modKey) {
        e.preventDefault();
        console.log('[useKeyboardShortcuts] Shift+F detected, calling onFullscreen');
        onFullscreen?.();
        return;
      }

      // Cmd/Ctrl + A - Select all (in block context)
      if (modKey && e.key === 'a' && isEditable) {
        // Let browser handle this naturally
        return;
      }

      // Cmd/Ctrl + C - Copy (custom handling if needed)
      if (modKey && e.key === 'c') {
        onCopy?.();
        // Don't prevent default - let browser copy
        return;
      }

      // Cmd/Ctrl + V - Paste (custom handling if needed)
      if (modKey && e.key === 'v' && isEditable) {
        // Custom paste handling can be added here
        onPaste?.();
        // Don't prevent default - let browser paste
        return;
      }

      // Shift + Enter on block (when focused but not editing) - Play from block start
      if (e.shiftKey && e.key === 'Enter' && !isEditable) {
        e.preventDefault();
        // This should be handled by the block component
        return;
      }
    },
    [
      enabled,
      isContentEditable,
      onSplit,
      onMergeUp,
      onMergeDown,
      onDelete,
      onDuplicate,
      onUndo,
      onRedo,
      onSave,
      onSearch,
      onPlayPause,
      onSkipForward,
      onSkipBackward,
      onNextBlock,
      onPreviousBlock,
      onMoveUp,
      onMoveDown,
      onCopy,
      onPaste,
      onFullscreen,
      skipForwardTime,
      skipBackwardTime,
      fineTuneTime,
    ]
  );

  /**
   * Attach/detach event listeners
   */
  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled, handleKeyDown]);

  return {
    isEnabled: enabled,
  };
};

export default useKeyboardShortcuts;


